import type { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';

import Footer from '@/components/footer';
import { graphcms, type IReview, REVIEW_QUERY } from '@/lib/hygraph';
import ReviewsSection from '@/modules/company/components/reviews-section';
import {
  CARDS,
  CONTENT_FAQS,
  CONTENT_KEY_FEATURES,
  CONTENT_LOGOS,
  CONTENT_SOLUTION_EMPOWERS,
  CONTENT_STATS,
} from '@/modules/solutions/constants/content';
import EmpowerSection from '@/modules/solutions/containers/empower-section';
import FaqSection from '@/modules/solutions/containers/faq-section';
import HeroSection from '@/modules/solutions/containers/hero-section';
import KeyFeaturesSection from '@/modules/solutions/containers/key-features-section';
import ProvideSection from '@/modules/solutions/containers/provide-section';
import StatsSection from '@/modules/solutions/containers/stats-section';
import SummarySection from '@/modules/solutions/containers/summary-section';

export default function Content({ reviews }: { reviews: IReview[] }) {
  return (
    <>
      <NextSeo
        title="E-Commerce Content Optimization & Compliance"
        description="Monitor the content of your products. Ensure accuracy in product titles, descriptions, and images for better consumer trust and retailer ranking."
      />

      <main className="overflow-hidden">
        <HeroSection
          infiniteScrollItems={CONTENT_LOGOS}
          title="E-Commerce Content Optimization & Compliance"
          infoSummary="Monitor the content of your products. Ensure accuracy in product titles, descriptions, and images for better consumer trust and retailer ranking."
          image={{
            src: '/images/solutions/content/hero.png',
            alt: 'E-Commerce Content Optimization & Compliance',
            width: 574,
            height: 350,
          }}
        />
        <StatsSection
          infoTitle="Mindsite optimizes content quality and compliance to ensure a strong brand image and market presence."
          stats={CONTENT_STATS}
        />
        <ProvideSection
          cards={CARDS}
          title={'What We Provide\n to Validate E-Commerce Content'}
          infoSummary="Mindsite Content Panel optimizes product content. You can ensure your product information meets retailer standards and enhances search visibility."
        />
        <KeyFeaturesSection
          title="Key Features of Mindsite Content Panel"
          features={CONTENT_KEY_FEATURES}
          video={{
            src: '/media/solutions/content.mp4',
            poster: '/media/solutions/content-poster.png',
            id: 'desktop-video-player',
          }}
        />
        <EmpowerSection
          title="Content Solution That Empowers Your E-Commerce Success"
          features={CONTENT_SOLUTION_EMPOWERS}
        />
        <ReviewsSection
          reviews={reviews}
          title="What Makes Us the Trusted Partner of Top Brands"
          titleClassname="text-xl font-semibold text-center [text-wrap:pretty] text-zinc-900 sm:text-2xl md:text-2xl lg:text-4xl/relaxed"
          sliderItemClassname="bg-solutions max-lg:[&>div]:flex-col max-lg:[&>div]:gap-10"
        />
        <FaqSection faqs={CONTENT_FAQS} />
        <SummarySection>
          <h2 className="text-xl font-semibold text-[#212427] sm:text-2xl lg:text-5xl/relaxed">
            Maximize Sales <br /> with Strategic Content Optimization
          </h2>
          <p className="text-xs text-zinc-600 sm:text-base lg:text-2xl">
            Optimize your e-commerce content for peak performance. Gain a
            competitive edge with detailed content analysis and compliance
            monitoring.
            <br />{' '}
            <span className="font-medium">
              Make your products stand out and increase your sales potential.
            </span>
          </p>
        </SummarySection>
      </main>
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  reviews: IReview[];
}> = async () => {
  try {
    const reviews = (await graphcms
      .request(REVIEW_QUERY)
      .then((res: any) => res.reviews)) as IReview[];

    return { props: { reviews } };
  } catch (error) {
    console.error(error);
  }

  return {
    props: {
      reviews: [],
    },
    notFound: true,
  };
};
